import { hoursBetween } from '../dateHelpers/adressDashboard/tasksHours'
import isAppointment from '../adressHelpers/taskKind'
import { ADRESS_STATUS_KEYS } from '../adressHelpers/adressStatusLabels'
import effectiveProjectStatus from '../effectiveProjectStatus'
import isFlagOn from '../isFlagOn'

// Derived, never copied: a hand-maintained list here whitelisted five statuses
// and silently dropped every household on 'Onbereikbaar' from the portfolio
// card — the columns stopped adding up to the project total (inbox #121).
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
export const HOUSEVISITS_STATUSES = Object.freeze([...ADRESS_STATUS_KEYS])

/**
 * De kindprojecten die meetellen op de FIXtool-portfoliokaarten: actief en met
 * de module huisbezoeken aan.
 *
 * 'Actief' via effectiveProjectStatus, dezelfde regel als het projectenoverzicht:
 * een demo-portfolio (ShowDemoProjects) telt zijn demolocaties als actief. Met
 * een eigen `Status === 'active'` toonde het overzicht 7 actieve locaties en
 * meldden de kaarten ernaast 'geen projecten met huisbezoeken'.
 * AI-CONTEXT: See docs/pitfalls.md#dev-second-source-never-reaches-its-own-gate
 *
 * @param {Array<{CompagnyID: string, Status?: string, HouseVisits?: boolean|string}>|undefined} projects
 * @param {boolean} showDemoProjects - CompagnyMeta.ShowDemoProjects van de portfolio
 * @returns {Array<{CompagnyID: string, Status?: string, HouseVisits?: boolean|string}>}
 */
export const selectHouseVisitsProjects = (projects, showDemoProjects) =>
    (Array.isArray(projects) ? projects : []).filter((p) =>
        effectiveProjectStatus(p, showDemoProjects) === 'active'
        // Via isFlagOn: de vlag staat ook als tekst opgeslagen.
        // AI-CONTEXT: See docs/pitfalls.md#ui-togglebutton-string-only-branches
        && isFlagOn(p.HouseVisits))

const emptyStatusBuckets = () =>
    HOUSEVISITS_STATUSES.reduce((acc, status) => {
        acc[status] = 0
        return acc
    }, {})

/**
 * @typedef {object} ProjectAdressBreakdown
 * @property {string} CompagnyID
 * @property {string} name
 * @property {number} total
 * @property {Record<string, number>} byStatus
 */

/**
 * @param {Array<{CompagnyID: string, CommunityName?: string}>} projects
 * @param {Array<{CompagnyID: string, Status?: string}>} adresses
 * @returns {Array<ProjectAdressBreakdown>}
 */
export const aggregateAdressesByProject = (projects, adresses) => {
    const projectList = Array.isArray(projects) ? projects : []
    const adressList = Array.isArray(adresses) ? adresses : []

    const byProject = new Map()
    for (const p of projectList) {
        if (!p?.CompagnyID) continue
        byProject.set(p.CompagnyID, {
            CompagnyID: p.CompagnyID,
            name: p.CommunityName || '—',
            total: 0,
            byStatus: emptyStatusBuckets(),
        })
    }

    for (const a of adressList) {
        const entry = byProject.get(a?.CompagnyID)
        if (!entry) continue
        entry.total += 1
        if (a.Status && Object.prototype.hasOwnProperty.call(entry.byStatus, a.Status)) {
            entry.byStatus[a.Status] += 1
        }
    }

    return Array.from(byProject.values())
}

/**
 * @typedef {object} ProjectVisitBreakdown
 * @property {string} CompagnyID
 * @property {string} name
 * @property {number} visitCount
 * @property {number} hours
 */

/**
 * @param {Array<{CompagnyID: string, CommunityName?: string}>} projects
 * @param {Array<{CompagnyID: string, Type?: string, Timestamp?: any, TimestampEnd?: any}>} tasks
 * @returns {Array<ProjectVisitBreakdown>}
 */
export const aggregateVisitsByProject = (projects, tasks) => {
    const projectList = Array.isArray(projects) ? projects : []
    const taskList = Array.isArray(tasks) ? tasks : []

    const byProject = new Map()
    for (const p of projectList) {
        if (!p?.CompagnyID) continue
        byProject.set(p.CompagnyID, {
            CompagnyID: p.CompagnyID,
            name: p.CommunityName || '—',
            visitCount: 0,
            hours: 0,
        })
    }

    for (const t of taskList) {
        // Niet op Type vergelijken: een subtaak draagt (tot de omzetting) hetzelfde
        // Type als zijn afspraak, en zo stond deze kaart bij Woonbrigade Haarlem
        // 24% te hoog. AI-CONTEXT: See docs/features/subtaken-bij-het-huishouden.md
        if (!isAppointment(t)) continue
        const entry = byProject.get(t.CompagnyID)
        if (!entry) continue
        entry.visitCount += 1
        entry.hours += hoursBetween(t.Timestamp, t.TimestampEnd)
    }

    return Array.from(byProject.values()).map(row => ({
        ...row,
        hours: Number(row.hours.toFixed(2)),
    }))
}

/**
 * Hoeveel WONINGEN zitten hierachter, en hoeveel daarvan worden door meer dan
 * één organisatie geholpen?
 *
 * Het antwoord is niet de som van de projecttotalen. Twee organisaties die
 * dezelfde woning helpen zitten allebei in hun eigen totaal — gemeten
 * 2026-09-03 toonde de kaart 4 waar er 3 waren. Zolang geen enkel portfolio
 * leden had die dezelfde huishoudens bedienen viel dat niet op; Energiearmoede
 * Arnhem is het eerste geval.
 *
 * Een huishouden ZONDER `HouseholdKey` telt als zichzelf, niet als 'gelijk aan
 * alle andere sleutelloze huishoudens'. Anders zou een portfolio met 200
 * niet-gegeocodeerde adressen er ineens 1 tellen.
 *
 * @param {Array<{ID?: string, CompagnyID?: string, HouseholdKey?: string}>} adresses
 * @returns {{unique: number, shared: number}}
 */
const countUniqueHouseholds = (adresses) => {
    /** @type {Map<string, {orgs: Set<string>, completed: boolean}>} */
    const perHousehold = new Map()

    for (const a of adresses) {
        if (!a) continue
        // Geen sleutel? Dan is dit dossier zijn eigen identiteit. Docid-niveau,
        // zodat twee onbekende woningen nooit samenvallen.
        const key = a.HouseholdKey || `__zonder-sleutel__:${a.CompagnyID || ''}:${a.ID || ''}`
        const entry = perHousehold.get(key) || { orgs: new Set(), completed: false }
        if (a.CompagnyID) entry.orgs.add(a.CompagnyID)
        // Eén afgerond dossier is genoeg. Zou het pas tellen als ÁLLE partijen
        // klaar zijn, dan zou een huishouden uit de telling verdwijnen op het
        // moment dat er een tweede organisatie bij komt — precies het
        // tegenovergestelde van wat er gebeurt.
        if (a.Status === 'completed') entry.completed = true
        perHousehold.set(key, entry)
    }

    let shared = 0
    let completed = 0
    for (const [key, entry] of perHousehold) {
        // Twee dossiers voor dezelfde woning BINNEN één organisatie is een
        // dubbel dossier, geen samenwerking — dat telt één keer maar heet niet
        // 'gedeeld'.
        if (!key.startsWith('__zonder-sleutel__') && entry.orgs.size > 1) shared += 1
        if (entry.completed) completed += 1
    }

    return { unique: perHousehold.size, shared, completed }
}

/**
 * @param {Array<ProjectAdressBreakdown>} adressRows
 * @param {Array<ProjectVisitBreakdown>} visitRows
 * @param {Array<object>} [adresses] - De RUWE adressen. Meegeven om te
 *   ontdubbelen; zonder deze parameter blijft `totalHouseholds` de som van de
 *   projecttotalen, zoals afnemers die hem niet kennen verwachten.
 * @returns {{ totalHouseholds: number, sharedHouseholds: number, totalCompleted: number, totalVisits: number, avgVisitsPerHousehold: number }}
 */
export const computeReachMetrics = (adressRows, visitRows, adresses) => {
    const adressList = Array.isArray(adressRows) ? adressRows : []
    const visitList = Array.isArray(visitRows) ? visitRows : []

    const kanOntdubbelen = Array.isArray(adresses)
    const { unique, shared, completed } = kanOntdubbelen
        ? countUniqueHouseholds(adresses)
        : { unique: 0, shared: 0, completed: 0 }

    const totalHouseholds = kanOntdubbelen
        ? unique
        : adressList.reduce((sum, r) => sum + (r.total || 0), 0)
    // Dit is de KOPTEKST van de portfoliokaart ('Huishoudens geholpen'), dus de
    // dubbeltelling doet hier het meeste kwaad.
    const totalCompleted = kanOntdubbelen
        ? completed
        : adressList.reduce((sum, r) => sum + (r.byStatus?.completed || 0), 0)
    const totalVisits = visitList.reduce((sum, r) => sum + (r.visitCount || 0), 0)
    const avgVisitsPerHousehold = totalHouseholds > 0
        ? Number((totalVisits / totalHouseholds).toFixed(2))
        : 0

    return { totalHouseholds, sharedHouseholds: shared, totalCompleted, totalVisits, avgVisitsPerHousehold }
}
